import { useRef, useState } from "react";
import type { Item } from "@iltocat/shared";
import { tabKeyStore } from "../lib/modelKey.js";
import {
  PROVIDERS,
  PROVIDER_IDS,
  loadProvider,
  rememberedModel,
  sendPhotos,
  type ProviderId,
} from "../lib/modelChoice.js";
import { loadModelPhotos, photosToSend } from "../lib/modelPhotos.js";
import { plural } from "../lib/api.js";
import { Icon } from "./Icon.js";
import { Notice } from "./Notice.js";

/**
 * The shortcut past the clipboard: one Ask button per provider that has a key
 * in Settings (`ModelKeysPanel`), sending the prompt and the items' photos and
 * handing the reply to the same review a pasted one gets. With no key, nothing
 * shows: the copy-and-paste route is the whole screen, as it always was.
 *
 * Each button says how many photos go, so what the model sees is never a
 * guess. The model is the one chosen in Settings, kept whole, so asking does
 * not list models again.
 */

type Stage = "photos" | "asking" | null;

export function AskModel({
  items,
  promptFor,
  onReply,
  onSettings,
}: {
  items: readonly Item[];
  /** The prompt, worded for whether photos go with it. */
  promptFor: (photosAttached: boolean) => string;
  onReply: (text: string) => void;
  onSettings: () => void;
}) {
  const [keys] = useState(() => tabKeyStore.read());
  const [stage, setStage] = useState<Stage>(null);
  const [asking, setAsking] = useState<ProviderId | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);

  const ready = PROVIDER_IDS.flatMap((provider) => {
    const key = keys[provider];
    const model = key ? rememberedModel(provider) : null;
    return key && model ? [{ provider, key, model }] : [];
  });
  if (ready.length === 0) return null;

  const photoCount = sendPhotos() ? photosToSend(items) : 0;

  async function ask(provider: ProviderId) {
    const entry = ready.find((option) => option.provider === provider);
    if (!entry) return;
    const abort = new AbortController();
    controller.current = abort;
    setAsking(provider);
    setProblem(null);
    setWarning(null);
    try {
      let photos: Awaited<ReturnType<typeof loadModelPhotos>> = { photos: [], failed: 0 };
      if (photoCount > 0 && entry.model.images) {
        setStage("photos");
        photos = await loadModelPhotos(items, abort.signal);
      }
      if (abort.signal.aborted) return;
      setStage("asking");
      const sent = photos.photos.some((set) => set.images.length > 0);
      const module = await loadProvider(provider);
      const answer = await module.ask(promptFor(sent), entry.key, entry.model, {
        photos: photos.photos,
        signal: abort.signal,
      });
      if (answer.ok) {
        if (photos.failed > 0)
          setWarning(`${plural(photos.failed, "photo")} would not load and was left out.`);
        onReply(answer.text);
      } else if (!abort.signal.aborted) {
        setProblem(answer.reason);
      }
    } catch {
      setProblem("Could not load that. Reload the page and try again.");
    } finally {
      controller.current = null;
      setStage(null);
      setAsking(null);
    }
  }

  return (
    <div className="ask-model">
      <div className="ask-model-buttons">
        {ready.map(({ provider, model }) =>
          asking === provider ? (
            <button
              key={provider}
              className="button button-quiet"
              type="button"
              onClick={() => controller.current?.abort()}
            >
              Stop
            </button>
          ) : (
            <button
              key={provider}
              className="button"
              type="button"
              disabled={asking !== null}
              title={model.name}
              onClick={() => void ask(provider)}
            >
              <Icon name="sparkles" size={18} />
              Ask {PROVIDERS[provider].assistant}
            </button>
          ),
        )}
      </div>
      <p className="muted small">
        {photoCount > 0 ? `With ${plural(photoCount, "photo")}. ` : ""}
        <button className="link" type="button" onClick={onSettings}>
          Models and keys
        </button>
      </p>
      {asking && (
        <p className="muted small">
          {stage === "photos" ? "Preparing photos…" : `Asking ${PROVIDERS[asking].assistant}…`}
        </p>
      )}
      {warning && <Notice tone="warn">{warning}</Notice>}
      {problem && <Notice tone="error">{problem}</Notice>}
    </div>
  );
}
